import { CircularProgress } from "@mui/material";
import React, { useState } from "react";
import { RiDeleteBin6Line } from "react-icons/ri";
import { useAddFileUrlMutation } from "src/services/FilePickerServices";
import { IMAGE_BASE_URL } from "src/utils/constants";
import { showToast } from "src/utils/showToaster";

type DropzoneProps = {
  onFileUpload: (file: string) => void;
  fileUrl: string;
  label?: string;
  required?: boolean;
};

type FileTypes = "images" | "pdfs";

export const getFileTypeFromUrl = (url: string) => {
  const regex = /uploads\/GEEKSDOOR\/([^/]+)\/.+$/;
  const match = url.match(regex);

  if (match && match[1]) {
    return match[1];
  } else {
    return "-";
  }
};

export const getFileNameFromUrl = (url: string) => {
  const regex = /uploads\/GEEKSDOOR\/(?:.+\/)*(.+)$/;
  const match = url.match(regex);

  if (match && match[1]) {
    return match[1];
  } else {
    return "-";
  }
};

const MOLDropzone: React.FC<DropzoneProps> = ({
  onFileUpload,
  fileUrl,
  label,
  required,
}) => {
  const [highlight, setHighlight] = useState<boolean>(false);

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setHighlight(true);
  };

  const handleDragLeave = () => {
    setHighlight(false);
  };
  const [uploadFile, { isLoading }] = useAddFileUrlMutation();
  const [file, setFile] = useState<any>(null);

  const onSelectFilePicker = async (file: any) => {
    setFile(file);
    const formData = new FormData();
    formData.append("bucketName", "GEEKSDOOR");
    formData.append("file", file, file?.name);

    try {
      const uploadResponse: any = await uploadFile(formData);
      const fileUrl = IMAGE_BASE_URL?.concat(
        `/${uploadResponse?.data?.file_path}`
      );
      onFileUpload(fileUrl || "");
      setFile(null);
    } catch (error: any) {
      showToast("error", error.message);
      setFile(null);
    }
  };

  const renderfilePreview = (fileType: FileTypes) => {
    switch (fileType) {
      case "images":
        return (
          <img
            className={`w-full h-full rounded ${isLoading && "opacity-40"}`}
            src={file ? URL.createObjectURL(file) : fileUrl}
            alt=""
          />
        );

      case "pdfs":
        return (
          <img
            className={`w-full h-full rounded ${isLoading && "opacity-40"}`}
            src={"/pdfImage.png"}
            alt=""
          />
        );

      default:
        break;
    }
  };

  const renderFileCard = () => {
    return (
      <div
        className={`flex items-center gap-4 shadow p-2 rounded border relative h-[75px] `}
      >
        <div
          className={`size-[55px] border-[2px] border-slate-100 rounded p-0.5 relative flex justify-center items-center`}
        >
          {renderfilePreview(getFileTypeFromUrl(fileUrl) as FileTypes)}
          {isLoading && (
            <div className="absolute">
              <CircularProgress size={20} />
            </div>
          )}
        </div>
        <div
          title={isLoading ? file?.name : getFileNameFromUrl(fileUrl)}
          className={`${
            isLoading && "opacity-40"
          } font-medium text-slate-700 flex-1 text-ellipsis overflow-hidden text-nowrap`}
        >
          {isLoading ? file?.name : getFileNameFromUrl(fileUrl)}
        </div>
        {!isLoading && (
          <button
            onClick={() => onFileUpload("")}
            title="Remove File"
            className="absolute p-1 text-white bg-red-500 rounded top-0.5 right-0.5"
          >
            <RiDeleteBin6Line size={14} />
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="">
      {label && (
        <label className="font-medium text-slate-700">
          {label} {required && <span className="text-red-500"> * </span>}
        </label>
      )}
      <div>
        {fileUrl || file ? (
          renderFileCard()
        ) : (
          <div
            className={`p-4 border-2 border-gray-300 border-dashed rounded-md h-[75px] ${
              highlight ? "border-blue-500" : ""
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={(e) => {
              e.preventDefault();
              onSelectFilePicker(e?.dataTransfer?.files[0]);
              setHighlight(false);
            }}
          >
            <p className="text-xs text-center">Drag & Drop file here or</p>
            <input
              id="fileInput"
              type="file"
              accept="image/*,.pdf"
              onChange={(e) => onSelectFilePicker(e?.target?.files?.[0])}
              className="hidden"
            />
            <button
              onClick={() => document.getElementById("fileInput")?.click()}
              className="block px-2 py-1 mx-auto mt-1 text-xs font-bold text-white bg-blue-500 rounded hover:bg-blue-700"
            >
              Upload File
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MOLDropzone;